import { UserId } from "@custom-types/base-types";
import {
  type AutoCompleteHelperText,
  type AutoCompleteOption,
  type AutoCompleteSelectedOption,
} from "@flat-ui";
import { EmailUtils } from "@stellar/utils";
import {
  type ValidateMessage,
  getSelectedOptionValues,
  validateInviteMembers,
} from "@utils/members-invite-utils";
import { Dispatch, SetStateAction, useState } from "react";

/** Minimal shape of an entity member needed to detect duplicates. */
interface ExistingMember {
  /** Unique identity of the member. */
  identity: string;

  /** Email of the member. */
  email: string;
}

interface UseMembersAutocompleteProps {
  /**
   * Display name of the entity members are invited to, e.g. "project" or "group".
   * Used to build the helper messages.
   */
  entityName: "project" | "group" | "snapshot" | "team" | "company";

  /** Members already belonging to the entity, used to detect duplicates. */
  existingMembers: ExistingMember[];

  /**
   * The full list of selectable options (members and teams). Used to decide
   * whether the currently typed text matches an existing option, so that the
   * invalid-email error is not shown while the user is typing a known name.
   */
  options: AutoCompleteOption[];

  /**
   * Identities of all the members in the company/workspace. Used to detect when
   * a selected value is a new email that would also be invited to the workspace.
   */
  companyMemberIds: UserId[];

  /**
   * Maximum number of members allowed per invite request. When omitted, no
   * limit is enforced (only the team flows cap the selection).
   */
  maxInvitedMembers?: number;

  /**
   * Message shown when the typed text neither matches an option nor is a valid
   * email.
   *
   * @default "Invalid Email or Doesn't match any team or member."
   */
  invalidInputMessage?: string;
}

interface UseMembersAutocompleteResult {
  /** Currently selected options (members, teams and new emails). */
  selectedMembers: AutoCompleteSelectedOption[];

  /** Updates the selected options directly (e.g. to preselect members). */
  setSelectedMembers: Dispatch<SetStateAction<AutoCompleteSelectedOption[]>>;

  /** The identity/email value for each selected option. Might include duplicates. */
  selectedValues: string[];

  /** The unique identity/email value for each selected option. Does not include duplicates. */
  uniqueSelectedValues: string[];

  /** Helper text to display below the autocomplete input. */
  helperText?: AutoCompleteHelperText;

  /** Whether the typed input and the current selection are both valid. */
  isMemberInputValid: boolean;

  /** Updates the selected options. */
  handleMemberSelect(options: AutoCompleteSelectedOption[]): void;

  /** Validates the typed text (invalid email / already a member). */
  onInputChange(value: string): void;

  /** Returns true when the email is valid and not already a member. */
  isNewEmailValid(value: string): boolean;

  /** Returns true when the value already belongs to the entity. */
  isAlreadyMember(value: string): boolean;

  /** Resets the selection and input validity. */
  reset(): void;
}

/**
 * Encapsulates the shared autocomplete workflow used when inviting members to a
 * project, group, snapshot or team (and when creating a group/team with
 * members). It manages the selected options and the validation messages while
 * leaving the entity specific submit logic to the consumer.
 *
 * Validation is split in two parts:
 * - `inputValidation` (state) reflects the text currently typed in the input.
 * - `selectionValidation` (derived) reflects the selected members (max limit,
 *   duplicates and new emails). Deriving it on every render keeps the message
 *   correct even when the autocomplete fires an input-change event right after
 *   a paste, which would otherwise clear the freshly computed message.
 *
 * The input message takes priority while the user is typing; otherwise the
 * selection-level message is shown.
 */
export function useMembersAutocomplete({
  entityName,
  existingMembers,
  options,
  companyMemberIds,
  maxInvitedMembers,
  invalidInputMessage = "Invalid Email or Doesn't match any team or member.",
}: UseMembersAutocompleteProps): UseMembersAutocompleteResult {
  const [selectedMembers, setSelectedMembers] = useState<
    AutoCompleteSelectedOption[]
  >([]);
  const [inputValidation, setInputValidation] = useState<ValidateMessage>({
    message: undefined,
    isValid: true,
  });

  const selectedValues = getSelectedOptionValues(selectedMembers);
  const uniqueSelectedValues = Array.from(new Set(selectedValues));

  // Selection-level validation (max limit, duplicates, new emails to
  // workspace). When `maxInvitedMembers` is not provided the selection is left
  // uncapped, which is the case for every flow except the team flows.
  const selectionValidation = validateInviteMembers({
    entityName,
    selectedOptions: selectedMembers,
    companyMemberIds,
    maxInvitedMembers,
  });

  const helperText = inputValidation.message ?? selectionValidation.message;
  const isMemberInputValid =
    inputValidation.isValid && selectionValidation.isValid;

  /** Returns true if the value already belongs to the entity members. */
  function isAlreadyMember(value: string): boolean {
    // Check if the value matches any existing member's identity or email
    // Both identity and email are checked to ensure that we catch duplicates regardless of which field the user inputs.
    return existingMembers.some(
      (member) =>
        member.identity === value ||
        member.email.toLowerCase() === value.toLocaleLowerCase().trim()
    );
  }

  /** Returns true if the new email is valid and not already a member. */
  function isNewEmailValid(value: string): boolean {
    return EmailUtils.isValidEmail(value) && !isAlreadyMember(value);
  }

  function handleMemberSelect(newOptions: AutoCompleteSelectedOption[]): void {
    setSelectedMembers(newOptions);
  }

  /** Validates the text currently typed in the autocomplete input. */
  function validateInput(value: string): ValidateMessage {
    if (!value) {
      return { message: undefined, isValid: true };
    }

    // The typed email already belongs to the entity.
    if (isAlreadyMember(value)) {
      return {
        message: {
          type: "info",
          text: `A member with the same email already exists in the ${entityName}`,
        },
        isValid: false,
      };
    }

    const lowerCasedValue = value.toLowerCase();
    const isValueInOptions = options.some(
      (option) =>
        option.title.toLowerCase().includes(lowerCasedValue) ||
        option.subtitle.toLowerCase().includes(lowerCasedValue)
    );

    // New text that matches neither an existing option nor a valid email.
    if (!isValueInOptions && !EmailUtils.isValidEmail(value)) {
      return {
        message: { type: "error", text: invalidInputMessage },
        isValid: false,
      };
    }

    return { message: undefined, isValid: true };
  }

  function onInputChange(value: string): void {
    setInputValidation(validateInput(value));
  }

  function reset(): void {
    setSelectedMembers([]);
    setInputValidation({ message: undefined, isValid: true });
  }

  return {
    selectedMembers,
    setSelectedMembers,
    selectedValues,
    uniqueSelectedValues,
    helperText,
    isMemberInputValid,
    handleMemberSelect,
    onInputChange,
    isNewEmailValid,
    isAlreadyMember,
    reset,
  };
}
